import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { CrmLead, CrmRoleOption } from '../../crm.model';
import { CrmService } from '../../crm.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Country } from '../../../models/common/country.model';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-lead-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    SelectButtonModule,
    BreadcrumbModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './lead-form.component.html',
  styleUrls: ['./lead-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeadFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  private messageService = inject(MessageService);

  public loading = signal(false);
  public saving = signal(false);
  public submitted = signal(false);
  public leadId = signal<number | null>(null);
  public countries = signal<Country[]>([]);
  public crmRoles = signal<CrmRoleOption[]>([]);
  public homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  public breadcrumbItems = signal<MenuItem[]>([]);

  public statuses = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Negotiation', value: 'NEGOTIATION' },
    { label: 'Lost', value: 'LOST' }
  ];

  public businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];

  public leadQualityOptions = [
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' }
  ];

  public leadForm = this.fb.group({
    id: [null as number | null],
    companyName: ['', Validators.required],
    customerNameLocal: [''],
    pocName: ['', Validators.required],
    pocEmail: ['', [Validators.email]],
    pocPhoneNumber: [''],
    mobileNumber: [''],
    status: ['NEW'],
    source: [''],
    leadQuality: ['MEDIUM'],
    businessType: ['B2B' as 'B2B' | 'B2C', Validators.required],
    natureOfBusiness: [''],
    roles: [['CUSTOMER'] as string[]],
    taxRegNumber: [''],
    crNumber: [null as number | null],
    street1: [''],
    street1Local: [''],
    city: [''],
    cityLocal: [''],
    postalCode: [''],
    countryId: [null as number | null],
    websiteURL: [''],
    companyId: [null as number | null],
    kamCustomerId: [null as number | null]
  }, { validators: [this.contactMethodValidator] });

  ngOnInit() {
    this.loadCountries();
    this.loadRoles();
    this.leadForm.get('businessType')?.valueChanges.subscribe(() => this.syncBusinessTypeValidators());
    this.syncBusinessTypeValidators();

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.leadId.set(id);
      this.setBreadcrumb('Edit Lead');
      this.loadLead(id);
      return;
    }

    this.setBreadcrumb('New Lead');
    this.leadForm.patchValue({
      status: 'NEW',
      leadQuality: 'MEDIUM',
      countryId: this.toolbarService.customer()?.company?.countryId ?? null,
      companyId: this.toolbarService.customer()?.company?.id ?? null
    });
  }

  loadCountries() {
    const cachedCountries = this.sessionStorageService.getCountryList() ?? [];
    if (cachedCountries.length) {
      this.countries.set(cachedCountries);
      this.restoreCountrySelection();
    }

    this.baseMasterDataService.getCountries('en-US').subscribe({
      next: countries => {
        if (countries?.length) {
          this.countries.set(countries);
          this.restoreCountrySelection();
        }
      },
      error: () => {
        if (!this.countries().length) {
          this.messageService.add({ severity: 'warn', summary: 'Countries', detail: 'Unable to load country list.' });
        }
      }
    });
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadLead(id: number) {
    this.loading.set(true);
    this.crmService.getLead(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        if (response.success && response.data) {
          this.leadForm.patchValue({
            ...response.data,
            countryId: this.numericId(response.data.countryId)
          });
          this.restoreCountrySelection();
          this.setBreadcrumb(response.data.leadNumber || 'Edit Lead');
          this.syncBusinessTypeValidators();
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Leads', detail: 'Unable to load lead.' })
    });
  }

  saveLead() {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    this.syncBusinessTypeValidators();

    if (this.leadForm.invalid) {
      this.leadForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }

    const leadData = this.leadForm.value as CrmLead;
    this.saving.set(true);

    if (leadData.id) {
      leadData.updatedByCustomerId = this.toolbarService.customer()?.id ?? leadData.updatedByCustomerId;
      this.crmService.updateLead(leadData).pipe(finalize(() => this.saving.set(false))).subscribe({
        next: response => {
          if (response.success) {
            this.messageService.add({ severity: 'success', summary: 'Lead Updated', detail: 'Lead details saved.' });
            this.router.navigate(['/crm/leads', response.data?.id ?? leadData.id]);
          }
        }
      });
      return;
    }

    leadData.companyId = this.toolbarService.customer()?.company?.id ?? leadData.companyId;
    leadData.createdByCustomerId = this.toolbarService.customer()?.id ?? leadData.createdByCustomerId;
    this.crmService.addLead(leadData).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: response => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Lead Created', detail: 'Lead has been added.' });
          this.router.navigate(['/crm/leads', response.data?.id]);
        }
      }
    });
  }

  cancel() {
    const id = this.leadForm.get('id')?.value;
    this.router.navigate(id ? ['/crm/leads', id] : ['/crm/leads']);
  }

  syncBusinessTypeValidators() {
    ['taxRegNumber', 'crNumber', 'postalCode'].forEach(controlName => {
      const control = this.leadForm.get(controlName);
      if (!control) return;
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  hasError(controlName: string): boolean {
    const control = this.leadForm.get(controlName);
    return !!control && control.invalid && (this.submitted() || control.touched || control.dirty);
  }

  hasContactMethodError(): boolean {
    const email = this.leadForm.get('pocEmail');
    const phone = this.leadForm.get('pocPhoneNumber');
    const mobile = this.leadForm.get('mobileNumber');
    return this.leadForm.hasError('contactMethod') &&
      (this.submitted() || !!email?.touched || !!phone?.touched || !!mobile?.touched || !!email?.dirty || !!phone?.dirty || !!mobile?.dirty);
  }

  private contactMethodValidator(control: AbstractControl): ValidationErrors | null {
    const email = String(control.get('pocEmail')?.value || '').trim();
    const phone = String(control.get('pocPhoneNumber')?.value || '').trim();
    const mobile = String(control.get('mobileNumber')?.value || '').trim();
    return email || phone || mobile ? null : { contactMethod: true };
  }

  private restoreCountrySelection() {
    const countryId = this.numericId(this.leadForm.get('countryId')?.value);
    if (!countryId) return;
    const selectedCountry = this.countries().find(country => Number(country.id) === countryId);
    this.leadForm.patchValue({ countryId: selectedCountry?.id ?? countryId }, { emitEvent: false });
  }

  private numericId(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private setBreadcrumb(currentLabel: string) {
    this.breadcrumbItems.set([
      { label: 'CRM', routerLink: '/crm/leads' },
      { label: 'Leads', routerLink: '/crm/leads' },
      { label: currentLabel }
    ]);
  }
}
